import React, { useState, useEffect } from 'react';
import { X, Save, ChevronDown, Plus, Trash2, Eye, Edit2 } from 'lucide-react';
import apiService from '@/services/api';

interface Section {
  id: number;
  page_slug: string;
  section_key: string;
  section_type: string;
  title?: string;
  subtitle?: string;
  body: string;
  display_order: number;
  is_active: boolean;
}

interface FullPageEditorModalProps {
  pageSlug: string;
  pageName: string;
  onClose: () => void;
  onSaved: () => void;
}

const FullPageEditorModal: React.FC<FullPageEditorModalProps> = ({
  pageSlug,
  pageName,
  onClose,
  onSaved
}) => {
  const [sections, setSections] = useState<Section[]>([]);
  const [editData, setEditData] = useState<Record<number, any>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set());
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    fetchPageSections();
  }, [pageSlug]);

  const fetchPageSections = async () => {
    try {
      setLoading(true);
      const response = await apiService.request(`/public/page-sections/${pageSlug}`);
      if (response.success && Array.isArray(response.data)) {
        const sortedSections = response.data.sort((a, b) => a.display_order - b.display_order);
        setSections(sortedSections);

        // Initialize edit data
        const initialData: Record<number, any> = {};
        sortedSections.forEach((section: Section) => {
          initialData[section.id] = {
            title: section.title,
            subtitle: section.subtitle,
            body: section.body
          };
        });
        setEditData(initialData);
      }
    } catch (err: any) {
      alert('Failed to load page sections: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (id: number) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedSections(newExpanded);
  };

  const updateSectionField = (id: number, field: string, value: string) => {
    setEditData(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value
      }
    }));
  };

  const addItemToSection = (id: number, sectionType: string) => {
    try {
      const data = editData[id];
      let current = [];
      try {
        current = JSON.parse(data.body || '[]');
      } catch (e) {
        current = [];
      }

      let newItem: any = {};
      switch (sectionType) {
        case 'hero':
          newItem = { title: 'Hero Title', subtitle: 'Subtitle', image: '', cta: 'Get Started' };
          break;
        case 'cards':
          newItem = { icon: '📦', title: 'New Card', description: 'Description' };
          break;
        case 'testimonials':
          newItem = { quote: 'Quote text', author: 'Author', role: 'Role' };
          break;
        case 'stats':
          newItem = { value: '100%', label: 'Label' };
          break;
        case 'features':
          newItem = { icon: '✨', title: 'Feature', description: 'Description' };
          break;
        case 'steps':
          newItem = { number: current.length + 1, title: 'Step', description: 'Description' };
          break;
        case 'faq':
          newItem = { question: 'Question?', answer: 'Answer' };
          break;
        case 'cta':
          newItem = { text: 'Button', url: '/', variant: 'primary' };
          break;
      }

      if (Array.isArray(current)) {
        current.push(newItem);
        updateSectionField(id, 'body', JSON.stringify(current, null, 2));
      }
    } catch (e) {
      alert('Error adding item');
    }
  };

  const removeItemFromSection = (id: number, index: number) => {
    try {
      const data = editData[id];
      let current = JSON.parse(data.body || '[]');
      if (Array.isArray(current)) {
        current.splice(index, 1);
        updateSectionField(id, 'body', JSON.stringify(current, null, 2));
      }
    } catch (e) {
      alert('Error removing item');
    }
  };

  const updateItemField = (id: number, itemIndex: number, field: string, value: any) => {
    try {
      const data = editData[id];
      let current = JSON.parse(data.body || '[]');
      if (Array.isArray(current) && current[itemIndex]) {
        current[itemIndex][field] = value;
        updateSectionField(id, 'body', JSON.stringify(current, null, 2));
      }
    } catch (e) {
      console.error('Error updating item', e);
    }
  };

  const handleSaveAll = async () => {
    try {
      setSaving(true);
      let hasError = false;

      // Save each modified section
      for (const section of sections) {
        const data = editData[section.id];
        if (!data) continue;

        try {
          // Validate JSON
          if (data.body.trim().startsWith('[')) {
            JSON.parse(data.body);
          }

          const response = await apiService.request(
            `/admin/page-sections/${section.id}`,
            'PUT',
            {
              title: data.title,
              subtitle: data.subtitle,
              body: data.body,
              is_active: true
            }
          );

          if (!response.success) {
            alert(`Error saving ${section.section_key}: ${response.message}`);
            hasError = true;
          }
        } catch (e: any) {
          alert(`Invalid JSON in ${section.section_key}: ${e.message}`);
          hasError = true;
        }
      }

      if (!hasError) {
        alert('✓ All sections saved successfully!');
        onSaved();
      }
    } finally {
      setSaving(false);
    }
  };

  const renderItemEditor = (section: Section, item: any, itemIndex: number) => {
    return (
      <div key={itemIndex} className="bg-gray-100 p-4 rounded border border-gray-300 mb-3">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-gray-700">Item {itemIndex + 1}</span>
          <button
            onClick={() => removeItemFromSection(section.id, itemIndex)}
            className="p-1 hover:bg-red-200 text-red-600 rounded transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-2">
          {Object.entries(item).map(([key, value]: [string, any]) => (
            <div key={key}>
              <label className="block text-xs font-medium text-gray-600 uppercase mb-1">
                {key}
              </label>
              {key.includes('description') || key === 'answer' || key === 'quote' ? (
                <textarea
                  value={value || ''}
                  onChange={(e) => updateItemField(section.id, itemIndex, key, e.target.value)}
                  rows={2}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              ) : (
                <input
                  type={typeof value === 'number' ? 'number' : 'text'}
                  value={value || ''}
                  onChange={(e) => updateItemField(section.id, itemIndex, key, e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder={key}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderSectionEditor = (section: Section) => {
    const data = editData[section.id];
    if (!data) return null;

    let parsedBody: any[] = [];
    try {
      if (data.body && (data.body.trim().startsWith('[') || data.body.trim().startsWith('{'))) {
        const parsed = JSON.parse(data.body);
        parsedBody = Array.isArray(parsed) ? parsed : [];
      }
    } catch (e) {
      parsedBody = [];
    }

    const isExpanded = expandedSections.has(section.id);
    const isArrayContent = Array.isArray(parsedBody) && parsedBody.length > 0;

    return (
      <div key={section.id} className="border border-gray-300 rounded-lg overflow-hidden bg-white">
        {/* Section Header */}
        <button
          onClick={() => toggleSection(section.id)}
          className="w-full bg-gray-50 p-4 flex items-center justify-between hover:bg-gray-100 transition-colors border-b border-gray-300"
        >
          <div className="text-left flex-1">
            <h4 className="font-semibold text-gray-900">{section.section_key}</h4>
            <p className="text-xs text-gray-600">{section.section_type}</p>
          </div>
          <ChevronDown
            className={`w-5 h-5 text-gray-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          />
        </button>

        {/* Section Content */}
        {isExpanded && (
          <div className="p-4 space-y-4 border-t border-gray-300">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                value={data.title || ''}
                onChange={(e) => updateSectionField(section.id, 'title', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="Section title"
              />
            </div>

            {/* Subtitle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
              <textarea
                value={data.subtitle || ''}
                onChange={(e) => updateSectionField(section.id, 'subtitle', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="Section subtitle"
                rows={2}
              />
            </div>

            {/* Items Editor */}
            {isArrayContent && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">Items</label>
                  <button
                    onClick={() => addItemToSection(section.id, section.section_type)}
                    className="px-2 py-1 bg-teal-600 text-white rounded text-xs hover:bg-teal-700 transition-colors flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3" />
                    Add
                  </button>
                </div>

                <div className="space-y-3">
                  {parsedBody.map((item: any, itemIndex: number) =>
                    renderItemEditor(section, item, itemIndex)
                  )}
                </div>
              </div>
            )}

            {/* Raw JSON Editor */}
            {!isArrayContent && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Content (JSON)</label>
                <textarea
                  value={data.body}
                  onChange={(e) => updateSectionField(section.id, 'body', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 font-mono text-xs"
                  rows={6}
                  placeholder="Enter JSON content..."
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-7xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Full Page Editor</h2>
            <p className="text-teal-100 text-sm">{pageName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-teal-800 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="bg-gray-50 border-b border-gray-200 flex gap-4 px-6 sticky top-0 z-10">
          <button
            onClick={() => setPreviewMode(false)}
            className={`py-3 px-4 font-medium transition-colors flex items-center gap-2 ${
              !previewMode
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Edit2 className="w-4 h-4" />
            Edit All Sections
          </button>
          <button
            onClick={() => setPreviewMode(true)}
            className={`py-3 px-4 font-medium transition-colors flex items-center gap-2 ${
              previewMode
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Eye className="w-4 h-4" />
            Preview
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="p-8 text-center">
              <p className="text-gray-600">Loading page sections...</p>
            </div>
          ) : !previewMode ? (
            <div className="p-6 max-w-4xl mx-auto space-y-4">
              {sections.length > 0 ? (
                sections.map(section => renderSectionEditor(section))
              ) : (
                <p className="text-center text-gray-600">No sections found</p>
              )}
            </div>
          ) : (
            <div className="p-6 max-w-4xl mx-auto space-y-6">
              {sections.map(section => {
                const data = editData[section.id];
                let displayBody = data?.body || '';

                // Try to parse as JSON for better formatting
                try {
                  if (displayBody && (displayBody.trim().startsWith('[') || displayBody.trim().startsWith('{'))) {
                    displayBody = JSON.stringify(JSON.parse(displayBody), null, 2);
                  }
                } catch (e) {
                  // Keep as plain text if not valid JSON
                }

                return (
                  <div key={section.id} className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                    <div className="mb-4">
                      <h3 className="text-xl font-bold text-gray-900">{data?.title}</h3>
                      {data?.subtitle && <p className="text-gray-600 mt-2">{data.subtitle}</p>}
                    </div>
                    {displayBody && (
                      <pre className="bg-white p-4 rounded border border-gray-300 text-xs overflow-auto max-h-48 whitespace-pre-wrap break-words">
                        {displayBody}
                      </pre>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t border-gray-200 p-6 flex items-center justify-end gap-3 sticky bottom-0">
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-700 font-medium hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveAll}
            disabled={saving}
            className="px-6 py-2 bg-teal-600 text-white font-medium hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save All Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FullPageEditorModal;
